import React, {
    CSSProperties,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { ReleaseStage } from '../../../../models/ReleaseStage';
import { Guid } from '../../../../../Common/models/Guid';
import { ThemeContext } from '../../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../../Common/util/localStorageUtils';
import { Deployment } from '../../../../models/Deployment';
import { Build } from '../../../../models/Build';
import {
    CommandButton,
    DocumentCardActivity,
    IconButton,
    IDocumentCardActivityStyles,
    IStackTokens,
    Link,
    Stack,
    Text,
} from '@fluentui/react';
import { CompletionIndicatorIcon } from '../Status/CompletionIndicatorIcon';
import { getStatusIconProperties } from '../../../../models/Timeline';
import { MobileReleaseStageEntityInfo } from './MobileReleaseStageEntityInfo';
import { ReleaseStageCompactDetails } from '../ReleaseStageCompactDetails';
import { AgrmDetailsPanel } from '../../../Deployment/Mobile/AgrmDetailsPanel';
import { AGRMDataType } from '../../../../models/AGRMDataType';
import { isValidCloudOrEnvironment } from '../../../../api/AGRMApi';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../../../../Common/api/ABHub/ABHubGlobalConfig';
import { ReleasePipelineSource } from '../../../../models/ReleasePipelineSource';

interface IMobileReleaseStageDetailsProps {
    isLoaded: boolean;
    stage: ReleaseStage | undefined;
    setSelected: Dispatch<SetStateAction<Deployment | Build | undefined>>;
    showPanel: () => void;
    showMetadataPanel: () => void;
    isPanelOpen: boolean;
    serviceId?: Guid;
    serviceTreeId?: Guid;
    pipelineSource?: ReleasePipelineSource;
    navigationTargetIdentifier?: string;
    navigationTargetStageIdentifier?: string;
    onNavigationComplete?: () => void;
}

export const MobileReleaseStageDetails: React.FC<IMobileReleaseStageDetailsProps> =
    (props: IMobileReleaseStageDetailsProps) => {
        const themeContext = useContext(ThemeContext);
        const theme = getThemeFromString(themeContext.themeName);

        const [stage, setStage] = useState<ReleaseStage>();
        const [expanded, setExpanded] = useState<boolean>();
        const rootRef = useRef<HTMLDivElement>(null);
        const [selectedAgrmOption, setSelectedAgrmOption] =
            useState<AGRMDataType>();
        const [selected, setSelected] = useState<
            Deployment | Build | undefined
        >();
        const [isAgrmPanelOpen, setIsAgrmPanelOpen] = useState<boolean>(false);
        const showAgrmPanel = () => setIsAgrmPanelOpen(true);
        const hideAgrmPanel = () => setIsAgrmPanelOpen(false);

        const divStyles = (status: string | undefined): CSSProperties => {
            let color = getStatusIconProperties(status, theme).iconColor;
            return {
                borderLeft: 'solid 1px ' + theme.palette.neutralLight,
                borderRight: 'solid 1px ' + theme.palette.neutralLight,
                borderBottom: 'solid 1px ' + theme.palette.neutralLight,
                boxShadow: '0 -8px 0 0 ' + color,
                backgroundColor: theme.palette.white,
                height: 'fit-content',
                padding: 16,
                margin: '10px 0px 20px 0px',
            };
        };

        const stackTokens: IStackTokens = { childrenGap: 10 };
        const activityStyles: Partial<IDocumentCardActivityStyles> = {
            root: { width: 200, backgroundColor: theme.palette.white },
        };

        // When parent component sends new data, refresh my state accordingly
        useEffect(() => {
            if (props.stage) {
                //set stage
                setStage(props.stage);

                if (props.stage.deployments.length > 0) {
                    props.stage.deployments.map((deployment) => {
                        deployment.showMetadata = true;
                    });
                }

                if (props.stage.builds.length > 0) {
                    props.stage.builds.map((build) => {
                        build.showMetadata = true;
                    });
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.stage, props.isLoaded]);

        useEffect(() => {
            const targetIdentifier = props.navigationTargetIdentifier;
            if (
                props.navigationTargetStageIdentifier !==
                stage?.releaseStageCorrelationId
            ) {
                return;
            }
            const containsTarget =
                targetIdentifier &&
                (stage?.deployments?.some(
                    (deployment) =>
                        deployment.uniqueIdentifier === targetIdentifier
                ) ||
                    stage?.builds?.some(
                        (build) => build.uniqueIdentifier === targetIdentifier
                    ));
            if (!containsTarget) {
                return;
            }
            if (!expanded) {
                setExpanded(true);
                return;
            }

            rootRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
            props.onNavigationComplete?.();
        }, [
            expanded,
            props.navigationTargetIdentifier,
            props.navigationTargetStageIdentifier,
            stage,
        ]);

        const expandRelease = () => {
            setExpanded(!expanded);
        };

        const showMetadataPanelClick = (entity: Deployment | Build) => {
            props.setSelected(entity);
            props.showMetadataPanel();
        };

        const showAgrmtDataClick = (
            deployment: Deployment,
            option: AGRMDataType
        ) => {
            setSelected(deployment);
            setSelectedAgrmOption(option);
            showAgrmPanel();
        };

        const environment = getConfig(GlobalConfigProperties.Environment);
        const cloud = getConfig(GlobalConfigProperties.Cloud);

        const isValidEnv = (cld: string, env: string) => {
            return isValidCloudOrEnvironment(cld, env);
        };

        const renderAgrmButton = (
            item: any,
            dismissMenu: (ev?: any, dismissAll?: boolean) => void
        ) => {
            return isValidEnv(cloud, environment) ? (
                <CommandButton
                    iconProps={item.iconProps}
                    text={item.text}
                    onClick={item.onClick}
                    styles={item.styles}
                />
            ) : (
                <></>
            );
        };

        return (
            <div ref={rootRef} className="MobileReleaseStageDetails-root">
                <div style={divStyles(stage?.completionIndicator)}>
                    {expanded ? (
                        <div>
                            <Stack tokens={stackTokens}>
                                <Stack
                                    horizontal
                                    tokens={stackTokens}
                                    style={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <IconButton
                                        iconProps={{
                                            iconName: expanded
                                                ? 'ChevronDownMed'
                                                : 'ChevronRightMed',
                                        }}
                                        onClick={expandRelease}
                                        ariaLabel={`${
                                            expanded ? 'Collapse' : 'Expand'
                                        } stage ${
                                            stage?.stageName ?? ''
                                        }`.trim()}
                                        aria-expanded={expanded}
                                        title={expanded ? 'Collapse' : 'Expand'}
                                    />

                                    <Text variant="medium">
                                        {stage?.stageName}
                                    </Text>
                                </Stack>
                                <CompletionIndicatorIcon
                                    completionIndicator={
                                        stage?.completionIndicator!
                                    }
                                    theme={theme}
                                    showText
                                />
                            </Stack>

                            <Stack
                                tokens={stackTokens}
                                style={{
                                    border:
                                        'solid 1px ' +
                                        theme.palette.neutralLighterAlt,
                                    background: theme.palette.neutralLighterAlt,
                                    padding: '10px',
                                    margin: '10px 0px',
                                }}
                            >
                                <Text variant="medium">Deployments</Text>
                                {stage?.deployments.map((deployment) => {
                                    return (
                                        <div
                                            style={{
                                                border:
                                                    'solid 1px ' +
                                                    theme.palette.neutralLight,
                                            }}
                                        >
                                            <MobileReleaseStageEntityInfo
                                                entity={deployment}
                                                setSelected={props.setSelected}
                                                showPanel={props.showPanel}
                                                monitorId={stage?.monitorId}
                                                serviceTreeId={
                                                    props.serviceTreeId
                                                }
                                                menuItems={[
                                                    {
                                                        key: 'showMetadata',
                                                        text: 'Show Metadata',
                                                        iconProps: {
                                                            iconName: 'Info',
                                                        },
                                                        onClick: () =>
                                                            showMetadataPanelClick(
                                                                deployment
                                                            ),
                                                    },
                                                    {
                                                        key: 'general',
                                                        text: 'General Deployment Data',
                                                        iconProps: {
                                                            iconName:
                                                                'KnowledgeArticle',
                                                        },
                                                        onRender:
                                                            renderAgrmButton,
                                                        onClick: () =>
                                                            showAgrmtDataClick(
                                                                deployment,
                                                                AGRMDataType.General
                                                            ),
                                                    },
                                                    {
                                                        key: 'orchestrations',
                                                        text: 'Deployment Orchestrations',
                                                        iconProps: {
                                                            iconName:
                                                                'ServerProcesses',
                                                        },
                                                        onRender:
                                                            renderAgrmButton,
                                                        onClick: () =>
                                                            showAgrmtDataClick(
                                                                deployment,
                                                                AGRMDataType.Orchestrations
                                                            ),
                                                    },
                                                    {
                                                        key: 'states',
                                                        text: 'Deployment States',
                                                        iconProps: {
                                                            iconName: 'Org',
                                                        },
                                                        onRender:
                                                            renderAgrmButton,
                                                        onClick: () =>
                                                            showAgrmtDataClick(
                                                                deployment,
                                                                AGRMDataType.States
                                                            ),
                                                    },
                                                ]}
                                            />
                                        </div>
                                    );
                                })}
                            </Stack>

                            <Stack
                                tokens={stackTokens}
                                style={{
                                    border:
                                        'solid 1px ' +
                                        theme.palette.neutralLighterAlt,
                                    background: theme.palette.neutralLighterAlt,
                                    padding: '10px',
                                    margin: '10px 0px',
                                }}
                            >
                                <Text variant="medium">Builds</Text>
                                {stage?.builds.map((build) => {
                                    return (
                                        <div
                                            style={{
                                                border:
                                                    'solid 1px ' +
                                                    theme.palette.neutralLight,
                                            }}
                                        >
                                            <MobileReleaseStageEntityInfo
                                                entity={build}
                                                setSelected={props.setSelected}
                                                showPanel={props.showPanel}
                                                menuItems={[
                                                    {
                                                        key: 'showMetadata',
                                                        text: 'Show Metadata',
                                                        iconProps: {
                                                            iconName: 'Info',
                                                        },
                                                        onClick: () => {
                                                            showMetadataPanelClick(
                                                                build
                                                            );
                                                        },
                                                    },
                                                ]}
                                            />
                                        </div>
                                    );
                                })}
                            </Stack>

                            <Stack horizontal>
                                {stage?.submittedBy && (
                                    <DocumentCardActivity
                                        styles={activityStyles}
                                        activity={'Submitted'}
                                        people={[
                                            {
                                                name: stage.submittedBy,
                                                profileImageSrc: '', //get from graph?
                                            },
                                        ]}
                                    />
                                )}
                                {stage?.approvedBy && (
                                    <DocumentCardActivity
                                        styles={activityStyles}
                                        activity={'Approved'}
                                        people={[
                                            {
                                                name: stage.approvedBy,
                                                profileImageSrc: '', //get from graph?
                                            },
                                        ]}
                                    />
                                )}
                                {stage?.monitorId && (
                                    <span>Monitor Id: {stage.monitorId}</span>
                                )}
                            </Stack>
                        </div>
                    ) : (
                        <div>
                            <Stack
                                horizontal
                                style={{
                                    alignItems: 'center',
                                }}
                            >
                                <IconButton
                                    styles={{
                                        root: {
                                            paddingRight: '5px',
                                        },
                                    }}
                                    iconProps={{
                                        iconName: expanded
                                            ? 'ChevronDownMed'
                                            : 'ChevronRightMed',
                                    }}
                                    onClick={expandRelease}
                                    ariaLabel={`${
                                        expanded ? 'Collapse' : 'Expand'
                                    } stage ${stage?.stageName ?? ''}`.trim()}
                                    aria-expanded={expanded}
                                    title={expanded ? 'Collapse' : 'Expand'}
                                />
                                <ReleaseStageCompactDetails stage={stage} />
                            </Stack>
                        </div>
                    )}
                </div>
                <AgrmDetailsPanel
                    deployment={selected as Deployment}
                    isOpen={isAgrmPanelOpen}
                    dismissPanel={hideAgrmPanel}
                    selectedOption={selectedAgrmOption ?? AGRMDataType.General}
                />
            </div>
        );
    };
